import {
    createRequestFilters,
    ICustomFilter,
    ListProviderChargemasterItemsOrderEnum,
    ListProviderChargemasterItemsRequest,
    ListProviderChargemastersRequest,
    ProviderChargemaster,
    ProviderChargemasterItem,
    ProviderChargemasterItemSortType,
} from '@enter/core';
import _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import type { ChargemasterGenerator } from './chargemaster.generator';
import { ChargemasterService } from './chargemaster.service';
import { IFeatures } from './root.store';
import { appendTo, equalsFilter } from './util';

export interface IChargemasterListing {
    chargemasters: ProviderChargemaster[];
    limit?: number;
    offset?: number;
    total?: number;
}

export interface IChargemasterListingRequest {
    page?: number;
    pageSize?: number;
    filters?: ICustomFilter[];
}

export interface IChargemasterDetailRequest {
    chargemasterId: string;
    page?: number;
    pageSize?: number;
    sortColumns?: ProviderChargemasterItemSortType[];
    sortOrders?: ListProviderChargemasterItemsOrderEnum[];
    filters?: ICustomFilter[];
}

export interface IChargemasterDetail {
    chargemasterItems?: ProviderChargemasterItem[];
    limit?: number;
    offset?: number;
    total?: number;
}

export class ChargemasterStore {
    @observable public chargemasters: ProviderChargemaster[] = [];
    @observable public chargemasterItems: ProviderChargemasterItem[] = [];

    private features: IFeatures;
    private chargemasterGenerator: ChargemasterGenerator;
    private chargemasterService: ChargemasterService = new ChargemasterService();

    constructor(features: IFeatures, chargemasterGenerator: ChargemasterGenerator) {
        this.features = features;
        this.chargemasterGenerator = chargemasterGenerator;
    }

    @action
    public async loadChargemasterListing(clr: IChargemasterListingRequest): Promise<IChargemasterListing> {
        const lpcr = clr2lpcr(clr);
        const listing = await (this.features.isDemo
            ? this.chargemasterGenerator.generateChargemasterListing(lpcr)
            : this.chargemasterService.fetchChargemasterListing(lpcr));
        if (!listing) {
            throw new Error(`No chargemasters available`);
        }
        const { chargemasters } = listing;
        runInAction(() => {
            this.chargemasters = appendTo<ProviderChargemaster>(this.chargemasters, chargemasters);
        });
        return listing;
    }

    @action
    public async loadChargemaster(chargemasterId: string): Promise<ProviderChargemaster | undefined> {
        const chargemaster = await (this.features.isDemo
            ? this.chargemasterGenerator.generateChargemaster(chargemasterId)
            : this.chargemasterService.fetchChargemaster(chargemasterId));
        if (!chargemaster) {
            throw new Error(`No chargemaster available for ID: ${chargemasterId}`);
        }
        runInAction(() => {
            this.chargemasters = appendTo<ProviderChargemaster>(this.chargemasters, chargemaster);
        });
        return chargemaster;
    }

    @action
    public async loadChargemasterDetail(cdr: IChargemasterDetailRequest): Promise<IChargemasterDetail> {
        const lpcir = cdr2lpcir(cdr);
        const detail = await (this.features.isDemo
            ? this.chargemasterGenerator.generateChargemasterDetail(lpcir)
            : this.chargemasterService.fetchChargemasterDetail(lpcir));
        if (!detail) {
            throw new Error(`No chargemaster items available`);
        }
        const { chargemasterItems } = detail;
        runInAction(() => {
            this.chargemasterItems = appendTo<ProviderChargemasterItem>(this.chargemasterItems, chargemasterItems);
        });
        return detail;
    }
}

// Convert from IChargemasterListingRequest to ListProviderChargemasterRequest
const clr2lpcr = (clr: IChargemasterListingRequest): ListProviderChargemastersRequest => {
    const filters = createRequestFilters(clr.filters, true);
    return {
        ...filters,
    };
};

// Convert from IChargemasterDetailRequest to ListProviderChargemasterItemsRequest
const cdr2lpcir = (cdr: IChargemasterDetailRequest): ListProviderChargemasterItemsRequest => {
    const limit = cdr.pageSize;
    const page = cdr.page;
    const offset = !_.isNil(limit) && page ? page * limit : undefined; // omit offset for page 0

    const sort = cdr.sortColumns;
    const order = _.isNil(sort) ? undefined : cdr.sortOrders;
    const filters = createRequestFilters(cdr.filters, true);

    // NB: always ignore inactive chargemaster items
    _.set(filters, 'filterActive', _.first(equalsFilter('1')));

    return {
        id: cdr.chargemasterId,
        limit,
        offset,
        order,
        sort,
        ...filters,
    };
};
